import { h } from 'preact';
import { createPortal } from 'preact/compat';
import { useState } from 'preact/hooks';
import { ShopDialog } from '../common/ShopDialog';
import { CartImage } from '../cart/CartImage';
import { OrthomolIcon } from '../../components-common/OrthomolIcon';
import { URLS } from '~/components-shop/Urls';
import { subscribeBackInStock, ShopwareApiError } from '../shopware/shopwareApi';

export interface BackInStockModalProps {
  /** O-Nummer of the out-of-stock variant the subscription is for */
  oNumber: string;
  /** Dosage form name, first line of the product row (Figma: text-dosage) */
  dosageFormName: string;
  /** Pack size label, second line of the product row (Figma: text-size) */
  packsizeLabel: string;
  /** Unmount callback, called after the dialog's fade-out */
  onClose: () => void;
}

type SubmitState = 'idle' | 'submitting' | 'success';

const DIALOG_ID = 'back-in-stock-modal';
const HEADING_ID = `${DIALOG_ID}-heading`;
const INPUT_ID = `${DIALOG_ID}-email`;
const ERROR_ID = `${DIALOG_ID}-error`;

/**
 * Map a failed subscribe call to the message shown under the input.
 * The Store API answers 400 for a missing/invalid e-mail and 404 when no product exists for the
 * O-Nummer (its `code` there is just "0", so we branch on the HTTP status, see shopwareApi.ts).
 */
const errorMessageFor = (error: unknown): string => {
  if (error instanceof ShopwareApiError) {
    if (error.status === 400) return 'Bitte gib eine gültige E-Mail-Adresse ein.';
    if (error.status === 404) return 'Für diese Variante ist leider keine Benachrichtigung möglich.';
  }
  return 'Das hat leider nicht geklappt. Bitte versuche es später noch einmal.';
};

/**
 * ORT-3036: "Wir sagen Dir Bescheid" – back-in-stock e-mail reminder for one out-of-stock variant.
 * Figma: reminder-modal 14742:80625 in 3OvjD4eOloCMBBbQ0gmWue. Content of a ShopDialog, rendered into
 * document.body while mounted; the parent mounts it on click and unmounts it via onClose.
 *
 * idle → submitting → success replaces the form with a confirmation (no separate Figma state, kept in
 * the same frame: same heading row, the product row stays, the copy confirms the address).
 * Errors keep the form and show the message under the input (Figma input helper text).
 */
export const BackInStockModal = ({ oNumber, dosageFormName, packsizeLabel, onClose }: BackInStockModalProps) => {
  // The address is read from the DOM on submit (uncontrolled input) and only kept in state for the success
  // text. A controlled `value` would let a parent re-render wipe browser autofill, which fills the field
  // without firing `input` (Safari in particular), and submit could then send an empty address.
  const [email, setEmail] = useState('');
  const [state, setState] = useState<SubmitState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (event: Event) => {
    event.preventDefault();
    if (state !== 'idle') return;

    const form = event.currentTarget as HTMLFormElement;
    const input = form.elements.namedItem('email') as HTMLInputElement | null;
    // Own validation message instead of the browser bubble (form has noValidate), same wording as the API's 400.
    if (!input || !input.validity.valid) {
      setErrorMessage('Bitte gib eine gültige E-Mail-Adresse ein.');
      input?.focus();
      return;
    }

    const address = input.value.trim();
    setEmail(address);
    setErrorMessage(null);
    setState('submitting');
    try {
      const result = await subscribeBackInStock(oNumber, address);
      // The plugin can answer 2xx with success:false (its unsubscribe route does so when nothing matched),
      // so a 200 alone is not a confirmed subscription.
      if (!result.success) throw new Error(`subscribe answered success=false for ${oNumber}`);
      setState('success');
    } catch (error) {
      console.error('[BackInStockModal] subscribe failed:', error);
      setErrorMessage(errorMessageFor(error));
      setState('idle');
    }
  };

  const productRow = (
    <div className="back-in-stock-modal__product d-flex align-items-center gap-2">
      <div className="back-in-stock-modal__thumbnail flex-shrink-0">
        <CartImage oNumberOrUniquePacksizeId={oNumber} mode="json" />
      </div>
      <div className="small lh-base">
        <div className="fw-bold">{dosageFormName}</div>
        <div>{packsizeLabel}</div>
      </div>
    </div>
  );

  return createPortal(
    <ShopDialog id={DIALOG_ID} labelledBy={HEADING_ID} onClose={onClose} className="back-in-stock-modal">
      {(close) =>
        state === 'success' ? (
          <div className="back-in-stock-modal__content pt-3" role="status">
            <h2 id={HEADING_ID} className="h5 mb-3 d-flex align-items-center gap-2">
              <OrthomolIcon name="check-24" size="24" />
              <span>Benachrichtigung aktiviert</span>
            </h2>
            <p className="small mb-3">
              Sobald das Produkt wieder verfügbar ist, bekommst Du einmalig eine E-Mail an <strong>{email}</strong>.
            </p>
            {productRow}
            <div className="back-in-stock-modal__actions">
              <button type="button" className="btn btn-primary w-100 justify-content-center" onClick={close}>
                Schließen
              </button>
            </div>
          </div>
        ) : (
          <div className="back-in-stock-modal__content pt-3">
            <h2 id={HEADING_ID} className="h5 mb-3">
              Wir sagen Dir Bescheid
            </h2>
            <p className="small mb-3">Sobald das Produkt wieder verfügbar ist, bekommst Du einmalig eine E-Mail.</p>
            {productRow}
            <form className="back-in-stock-modal__actions" noValidate onSubmit={handleSubmit}>
              <div className="mb-3">
                <label htmlFor={INPUT_ID} className="visually-hidden">
                  E-Mail Adresse
                </label>
                <input
                  id={INPUT_ID}
                  name="email"
                  type="email"
                  className={`form-control${errorMessage ? ' is-invalid' : ''}`}
                  placeholder="E-Mail-Adresse"
                  autoComplete="email"
                  inputMode="email"
                  required
                  disabled={state === 'submitting'}
                  aria-describedby={errorMessage ? ERROR_ID : undefined}
                  aria-invalid={errorMessage ? 'true' : undefined}
                  onInput={() => {
                    if (errorMessage) setErrorMessage(null);
                  }}
                />
                {errorMessage && (
                  <p id={ERROR_ID} className="small text-danger mt-1 mb-0" role="alert">
                    {errorMessage}
                  </p>
                )}
              </div>
              <button
                type="submit"
                className="btn btn-primary w-100 justify-content-center mb-3"
                disabled={state === 'submitting'}
                aria-busy={state === 'submitting'}
              >
                {state === 'submitting' ? (
                  <>
                    <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                    <span className="visually-hidden">Wird gesendet</span>
                  </>
                ) : (
                  <span>Benachrichtige mich</span>
                )}
              </button>
              <p className="small mb-0">
                Du erhältst ausschließlich eine einmalige Benachrichtigung, sobald das Produkt wieder verfügbar ist, keinen Newsletter. Deine E-Mail-Adresse verwenden wir nur zu diesem Zweck.{' '}
                <a href={URLS.datenschutz} target="_blank" rel="noopener">
                  Datenschutzhinweise
                </a>
              </p>
            </form>
          </div>
        )
      }
    </ShopDialog>,
    document.body
  );
};
